
import { cn } from "@/lib/utils";
import { ChartLine, Heart, Brain, Link as LinkIcon, ShieldAlert, BarChart3 } from "lucide-react";

export function FeaturesSectionWithHoverEffects() {
  const features = [
    {
      title: "See Tomorrow's Energy Today",
      description: "Wake up to a personalized forecast showing your Power, Focus, and Drift Zones. Schedule your investor pitch at 10 AM (your peak) instead of 3 PM (your crash).",
      icon: <ChartLine className="h-6 w-6" />,
    },
    {
      title: "Your Oura/WHOOP Data, Finally Useful",
      description: "ChronoCal turns your sleep and HRV data into actionable scheduling decisions. Poor sleep? Your calendar automatically suggests lighter meeting loads.",
      icon: <Heart className="h-6 w-6" />,
    },
    {
      title: "Four Zones, Zero Guesswork",
      description: "Power Zones for strategic decisions. Focus Zones for deep work. Drift Zones for creative brainstorming. Rest Zones for recovery. ChronoCal maps your day automatically.",
      icon: <Brain className="h-6 w-6" />,
    },
    {
      title: "Your Booking Page Protects Your Peak Hours",
      description: "Share a link. Clients book. But they only see your Power and Focus Zones—never your afternoon energy crashes. You stay sharp for every meeting.",
      icon: <LinkIcon className="h-6 w-6" />,
    },
    {
      title: "Sees Burnout Before You Do",
      description: "After intense work periods, ChronoCal automatically suggests lighter meeting loads and recovery blocks based on your HRV and sleep data.",
      icon: <ShieldAlert className="h-6 w-6" />,
    },
    {
      title: "Performance Insights That Actually Help",
      description: "Track productivity patterns and see exactly which times of day yield your best work. Continuously optimize your schedule for peak performance.",
      icon: <BarChart3 className="h-6 w-6" />,
    },
  ];
  
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 relative z-10 py-10 max-w-7xl mx-auto">
      {features.map((feature, index) => (
        <Feature key={feature.title} {...feature} index={index} />
      ))}
    </div>
  );
}

const Feature = ({
  title,
  description,
  icon,
  index,
}: {
  title: string;
  description: string;
  icon: React.ReactNode;
  index: number;
}) => {
  return (
    <div
      className={cn(
        "flex flex-col lg:border-r py-10 relative group/feature dark:border-neutral-800",
        (index === 0 || index === 3) && "lg:border-l dark:border-neutral-800",
        index < 3 && "lg:border-b dark:border-neutral-800"
      )}
    >
      {index < 3 && (
        <div className="opacity-0 group-hover/feature:opacity-100 transition duration-200 absolute inset-0 h-full w-full bg-gradient-to-t from-neutral-100 dark:from-neutral-800 to-transparent pointer-events-none" />
      )}
      {index >= 3 && (
        <div className="opacity-0 group-hover/feature:opacity-100 transition duration-200 absolute inset-0 h-full w-full bg-gradient-to-b from-neutral-100 dark:from-neutral-800 to-transparent pointer-events-none" />
      )}
      <div className="mb-4 relative z-10 px-10 text-neutral-600 dark:text-neutral-400">
        {icon}
      </div>
      <div className="text-lg font-bold mb-2 relative z-10 px-10">
        <div className="absolute left-0 inset-y-0 h-6 group-hover/feature:h-8 w-1 rounded-tr-full rounded-br-full bg-neutral-300 dark:bg-neutral-700 group-hover/feature:bg-blue-500 transition-all duration-200 origin-center" />
        <span className="group-hover/feature:translate-x-2 transition duration-200 inline-block text-neutral-800 dark:text-neutral-100">
          {title}
        </span>
      </div>
      <p className="text-sm text-neutral-600 dark:text-neutral-300 max-w-xs relative z-10 px-10">
        {description}
      </p>
    </div>
  );
};
